import React from 'react';
import {
	Box,
	HStack,
	VStack,
	Flex,
	Heading,
	Input,
	InputGroup,
	Spinner,
	List,
	Separator,
	Link,
	Text,
	Switch,
	Button,
	Spacer,
	Image,
	Dialog,
	CloseButton,
	Icon as ChakraIcon,
	Portal
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { FaArrowLeft, FaArrowRight, FaSearch, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { VariableSizeList, FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { addRecentProject, getRecentProjects } from '../lib/recentProjects';
import debounce from 'lodash/debounce';
import { getPacket, getPackets } from '../lib/getPackets';
import getPhase from '../lib/getPhase';
import submitPacket from '../lib/submitPacket';
import assignPacketToProject from '../lib/assignPacketToProject';
import createNewPacket from '../lib/createNewPacket';
import createNewForm from '../lib/createNewForm';
import savePacketForm from '../lib/savePacketForm';
import createDataAPI from '../lib/createDataAPI';
import { emailDriverExemptions, sendReassignmentConfirmation } from '../lib/email';
import { getWebforms } from '../lib/webforms';
import ChangePacketDate from '../components/ChangePacketDate';
import FormEditor from './FormEditor';
import FormAttachments from './FormAttachments';
import SafetyDatabase from './SafetyDatabase';
import DriverExemptionForm from '../forms/DriverExemptionForm';
import { loadWebforms } from '../store/webforms/webforms.actions';
import theme from '../theme';

if (window.location.pathname === '/') {
	window.history.replaceState(window.history.state, '', `${window.location.origin}/dashboard`);
} else if (window.location.pathname === '/dashboard/safety-database' && !window.canAccessSafetyDatabase) {
	window.location.assign(`${window.location.origin}/dashboard`);
}

const recentProjects = getRecentProjects();

const tabLabels = {
	'project-forms': 'Project Forms',
	unassigned: 'Unassigned Packets',
	'safety-database': 'Safety Database'
};

const PATH_TAB = {
	'/': 'project-forms',
	'/dashboard': 'project-forms',
	'/dashboard/unassigned': 'unassigned',
	'/dashboard/safety-database': 'safety-database'
};

const refreshAuthTokens = async () => {
	console.log('refreshing tokens...');

	const token = await axios
		.get('/jwt/token?_format=json')
		.then(({ data: { token } }) => token)
		.catch(Sentry.captureException);

	const csrf = await axios
		.get('/session/token')
		.then(({ data }) => data)
		.catch(Sentry.captureException);

	return { token, csrf };
};

const dbounce = debounce(fn => fn(), 1000);

const isSubmitted = packet => packet?.submitted === true || packet?.submitted === 'true' || packet?.submitted === '1';

export default function Dashboard({ navWidth }) {
	const dispatch = useDispatch();

	// Navigation states
	const [tab, setTab] = React.useState(window.tab);
	const [windowSize, setWindowSize] = React.useState(window.innerWidth);

	// App states
	const [currentProject, setCurrentProject] = React.useState('');
	const [currentPhase, setCurrentPhase] = React.useState({});
	const [currentPacketId, setCurrentPacketId] = React.useState('');
	const [currentFormId, setCurrentFormId] = React.useState('');
	const [attachmentsIndex, setAttachmentsIndex] = React.useState(null);
	const [assignPacket, setAssignPacket] = React.useState(false);
	const [driverExemptionListOpen, setDriverExemptionListOpen] = React.useState(false);
	const [currentDriverExemption, setCurrentDriverExemption] = React.useState(null);
	const [projectLevelFormsOpen, setProjectLevelFormsOpen] = React.useState(false);
	const [dailyPacketsListOpen, setDailyPacketsListOpen] = React.useState(false);
	const [error, setError] = React.useState(null);
	const [errorDialog, setErrorDialog] = React.useState(null);
	const [signatureDialogOpen, setSignatureDialogOpen] = React.useState(false);
	const [signatureMissingDialogOpen, setSignatureMissingDialogOpen] = React.useState(false);
	const [loadMoreError, setLoadMoreError] = React.useState(null);
	const [searchQuery, setSearchQuery] = React.useState('');
	const [searchArchives, setSearchArchives] = React.useState(false);
	const [packetList, setPacketList] = React.useState([]);
	const [packetListLoading, setPacketListLoading] = React.useState(false);
	const [morePacketsLoading, setMorePacketsLoading] = React.useState(false);
	const [supervisorPacketId, setSupervisorPacketId] = React.useState('');
	const [activeLog, setActiveLog] = React.useState('');
	const [formOptionsDialog, setFormOptionsDialog] = React.useState(false);
	const [packetOptionsDialog, setPacketOptionsDialog] = React.useState(false);
	const [assignTo, setAssignTo] = React.useState({});
	const [assignConfirmation, setAssignConfirmation] = React.useState(false);
	const [calendar, setCalendar] = React.useState(false);
	const [navDrawerOpen, setNavDrawerOpen] = React.useState(true);
	const [submitConfirmation, setSubmitConfirmation] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);
	const [submissionStatus, setSubmissionStatus] = React.useState(null);
	const [negativeTimeLogAlert, setNegativeTimeLogAlert] = React.useState(false);
	const [editMode, setEditMode] = React.useState(false);
	const [saveChanges, setSaveChanges] = React.useState([]);
	const [confirmChanges, setConfirmChanges] = React.useState(false);
	const [nextNavigation, setNextNavigation] = React.useState(null);

	const errorModalRef = React.useRef();
	const signatureRef = React.useRef();
	const signatureMissingRef = React.useRef();
	const variableSizeListRef = React.useRef();
	const assignPacketRef = React.useRef();
	const negativeTimeLogRef = React.useRef();
	const submitPacketRef = React.useRef();
	const submissionStatusRef = React.useRef();
	const confirmChangesRef = React.useRef();

	if (!Object.keys(tabLabels).includes(tab)) {
		return (
			<Box>
				<h1>Page not found</h1>
			</Box>
		);
	}

	const resizeWindow = () => setWindowSize(window.innerWidth);

	const navEnabled = React.useMemo(() => tab !== 'safety-database', [tab]);

	const navDrawerEnabled = React.useMemo(() => navEnabled && windowSize < 1300, [windowSize, navEnabled]);

	const navigationList = React.useMemo(() => {
		const projectList = window.projectList || [];

		if (searchQuery) {
			const projects = {};

			const list = projectList.filter(
				project =>
					(project?.project_name?.toLowerCase().includes(searchQuery.toLocaleLowerCase()) ||
						project?.project_number?.toLowerCase().includes(searchQuery.toLocaleLowerCase()) ||
						project?.phase_name?.toLowerCase().includes(searchQuery.toLocaleLowerCase()) ||
						project?.phase_number?.toLowerCase().includes(searchQuery.toLocaleLowerCase()) ||
						`${project?.project_number?.toLowerCase()}-${project?.phase_number?.toLowerCase()}`.includes(
							searchQuery.toLocaleLowerCase()
						)) &&
					(searchArchives || project.status === 'A')
			);

			if (tab === 'project-forms' && !assignPacket) {
				list.forEach(project => {
					const { project_number } = project;

					if (!projects[project_number]) {
						projects[project_number] = [];
					}

					projects[project_number].push(project);
				});

				return Object.entries(projects);
			} else {
				return list;
			}
		} else if (tab === 'project-forms' && !assignPacket) {
			return recentProjects.map(project => [
				project,
				projectList.filter(({ project_number }) => project_number === project)
			]);
		} else {
			return projectList;
		}
	}, [searchQuery, searchArchives, tab, assignPacket]);

	const supervisorPacket = React.useMemo(
		() => packetList.find(({ packet_id }) => packet_id === supervisorPacketId),
		[supervisorPacketId, packetList]
	);

	const supervisorPacketFormsList = React.useMemo(
		() => supervisorPacket?.packet_forms?.map(({ form_id }) => form_id) || [],
		[supervisorPacketId, supervisorPacket?.packet_forms?.length]
	);

	const [dailyPacketsLength, dailyPacketsHeight] = React.useMemo(() => {
		const dailyPacketsLength = packetList.filter(({ packet_tag }) => packet_tag !== 'supervisor').length;

		const MAX_TEXTBOX_WIDTH = 471;
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		context.font = '400 15px helvetica';

		const dailyPacketsHeight = packetList.reduce((acc, { packet_tag, title }) => {
			if (packet_tag !== 'supervisor') {
				const metrics = context.measureText(title);
				const extraTextBoxHeight = Math.floor(metrics.width / MAX_TEXTBOX_WIDTH) * 20;
				acc += 36 + extraTextBoxHeight;
			}
			return acc;
		}, 0);

		canvas.remove();

		return [dailyPacketsLength, dailyPacketsHeight];
	}, [packetList]);

	// Get currently selected packet
	const [currentPacket, currentPacketSubmitted] = React.useMemo(() => {
		const currentPacket = packetList.find(({ packet_id }) => packet_id === currentPacketId);
		const currentPacketSubmitted = isSubmitted(currentPacket);

		return [currentPacket, currentPacketSubmitted];
	}, [currentPacketId, packetList]);

	// Jacob NOTE: This variable cannot be memoized, it will give unexpected behavior specifically with getting handoffData
	// and will give the formData of the currentForm instead of supervisor_handoff.
	// There could also be potential other issues if this is memoized, but this is what I found.
	const dataAPI = createDataAPI({
		phase: currentPhase,
		packet: currentPacket
	});

	// Get currently selected form
	const [currentForm, formData, formDefinition] = React.useMemo(() => {
		const currentForm = currentPacket?.packet_forms?.find(
			({ form_id }) => form_id === (currentFormId ?? 'supervisor_handoff')
		);

		const formData = JSON.parse(currentForm?.form_data || '{}');
		const formDefinition = JSON.parse(currentForm?.form_definition || '{}');

		return [currentForm, formData, formDefinition];
	}, [currentPacket, currentFormId]);

	const [handoffData, lotoData, projectInfoCrew, notReq] = React.useMemo(() => {
		const handoffData = dataAPI.getFormData('supervisor_handoff');
		const deltaPData = dataAPI.getFormData('delta_p_list');
		const lotoData = dataAPI.getFormData('hazardous_energy_loto');

		const piCrew = new Set();
		const crewRoles = {};
		let piCrewSize = 0;

		[
			...(handoffData?.employee_crew_members || []),
			...(handoffData?.non_employee_crew_members || []),
			...(handoffData?.crew_members || [])
		].forEach(cm => {
			piCrewSize++;
			piCrew.add(cm.employee_crew_member_name || cm.non_employee_crew_member_name || cm.crew_member_name);

			if (cm.employee_crew_member_name) {
				crewRoles[cm.employee_crew_member_name] = cm.employee_crew_member_role;
			} else if (cm.non_employee_crew_member_name) {
				crewRoles[cm.non_employee_crew_member_name] = cm.non_employee_crew_member_role;
			} else if (cm.crew_member_name) {
				crewRoles[cm.crew_member_name] = cm.crew_member_role;
			}
		});

		const projectInfoCrew = { crew: piCrew, size: piCrewSize, crewRoles };

		// All forms are required by default,
		// this is a whitelist to not require forms
		let notReq =
			handoffData.project_type === 'OVERHEAD'
				? new Set([
						'delta_p_field_list',
						'pre_dive_meeting',
						'pre_dive_equipment_safety_list',
						'dive_log',
						'loadout_list',
						'daily_field_record',
						'supervisor_handoff'
					])
				: handoffData.mob_only
					? new Set([
							'delta_p_field_list',
							'pre_dive_meeting',
							'pre_dive_equipment_safety_list',
							'dive_log',
							'loadout_list',
							'general_daily_toolbox'
						])
					: handoffData.diving
						? new Set(['general_daily_toolbox'])
						: new Set([
								'pre_dive_meeting',
								'pre_dive_equipment_safety_list',
								'delta_p_field_list',
								'dive_log'
							]);

		if (!lotoData?.lockout_tagout_required?.yes) {
			notReq.add('hazardous_energy_loto');
		}

		if (!deltaPData?.complete) {
			notReq.add('delta_p_list');
		}

		return [handoffData, lotoData, projectInfoCrew, notReq];
	}, [dataAPI, currentPhase, currentPacket]);

	const [currentPacketValidation, isPacketValid] = React.useMemo(() => {
		const currentPacketValidation =
			currentPacket?.packet_forms?.reduce((acc, form) => {
				if (currentPacketSubmitted) {
					acc[form.form_id] = true;
					return acc;
				}

				let isValid = true;
				let requiredFields = {};
				let currentFormData = JSON.parse(form.form_data || '{}');

				const currentFormDefinition = JSON.parse(form.form_definition);

				// Get an object of all field keys with required status values
				const traverseDefTree = (tree, field) => {
					const childFields = Object.keys(tree).filter(childField => childField[0] !== '#');

					if (tree['#type']) {
						if (childFields.length > 0) {
							childFields.map(childField => traverseDefTree(tree[childField], childField));
						} else {
							requiredFields[field] = tree['#required'] || false;
						}
					}
				};

				traverseDefTree(currentFormDefinition.elements.form_root);

				Object.entries(currentFormData)
					// Filter for required fields only
					.filter(([fieldKey, fieldValue]) => Array.isArray(fieldValue) || requiredFields[fieldKey])
					.forEach(([, fieldValue]) => {
						let dataItems = [];

						// Deep traversal through field value data structures
						if (Array.isArray(fieldValue)) {
							fieldValue.forEach(fieldItem => {
								if (typeof fieldItem === 'object') {
									Object.entries(fieldItem)
										.filter(([itemKey]) => requiredFields[itemKey])
										.forEach(([, itemValue]) => dataItems.push(fieldItem[itemValue]));
								} else {
									dataItems = fieldValue;
								}
							});
						} else {
							dataItems.push(fieldValue);
						}

						dataItems.forEach(dataItem => {
							switch (typeof dataItem) {
								case 'undefined':
									break;
								case 'object':
									isValid = false;
									break;
								case 'string':
									if (!dataItem) {
										isValid = false;
									}
									break;
								case 'boolean':
									break;
								default:
									break;
							}
						});
					});

				if (!notReq.has(form.form_id)) {
					switch (form.form_id) {
						case 'supervisor_handoff':
							isValid = [
								...(handoffData?.employee_crew_members || []),
								...(handoffData?.non_employee_crew_members || []),
								...(handoffData?.crew_members || [])
							].reduce(
								(acc, cm) =>
									!acc
										? acc
										: !(
												!(
													cm.employee_crew_member_name ||
													cm.non_employee_crew_member_name ||
													cm.crew_member_name
												) ||
												!(
													cm.employee_crew_member_role ||
													cm.non_employee_crew_member_role ||
													cm.crew_member_role
												)
											),
								true
							);

							if (isValid) {
								const timeItems = currentFormData?.time_items || [];
								isValid = timeItems.reduce((acc, { task = '' }) => {
									return !task ? false : acc;
								}, true);
							}

							if (
								Object.prototype.hasOwnProperty.call(currentFormData, 'per_diems') &&
								!currentFormData?.per_diems?.task
							) {
								isValid = false;
							}

							if (
								Object.prototype.hasOwnProperty.call(currentFormData, 'units') &&
								!currentFormData?.units?.task
							) {
								isValid = false;
							}

							break;
						case 'loadout_list':
							isValid = !!currentFormData?.approval_timestamp;

							break;
						case 'daily_field_record':
							if (
								currentFormData?.description_of_work === '' ||
								currentFormData?.supervisor_name === '' ||
								currentFormData?.supervisor_signature === null ||
								currentFormData?.equipment?.filter(
									equipmentItem =>
										(equipmentItem.phase && (!equipmentItem.task || equipmentItem.task === '')) ||
										(equipmentItem.measurement === 'miles' && equipmentItem.quantity === '')
								)?.length > 0
							) {
								isValid = false;
							}

							if (isValid) {
								isValid =
									currentFormData?.crew_members?.length === projectInfoCrew?.size &&
									currentFormData?.crew_members?.reduce(
										(acc, { name, role }) =>
											!acc
												? acc
												: Boolean(
														projectInfoCrew.crew.has(name) &&
															(role
																? role === projectInfoCrew.crewRoles[name]
																: !projectInfoCrew.crewRoles[name])
													),
										true
									);
							}

							if (isValid) {
								isValid = currentFormData?.equipment?.every(
									({ quantity }) => quantity && parseInt(quantity) > 0
								);
							}

							break;
						case 'pre_dive_equipment_safety_list':
							isValid = !!currentFormData?.complete;

							break;
						case 'pre_dive_meeting':
							if (Object.keys(currentFormData || {}).length <= 0) {
								currentFormData = {
									dive_mode: {},
									hazards: {},
									protective_equipment: {},
									conditions: {},
									team_assessment: {},
									crew: {},
									emergency_procedures: {}
								};
							}

							if (currentFormData?.hazards?.known_hazards === '') {
								isValid = false;
							}

							if (
								currentFormData?.dive_mode?.surface_supplied_air === false &&
								currentFormData?.dive_mode?.scuba === false &&
								currentFormData?.dive_mode?.wading === false
							) {
								isValid = false;
							}

							if (
								currentFormData?.dive_mode?.objective_of_dive === '' ||
								currentFormData?.hazards?.safety_observations_discussed !== true
							) {
								isValid = false;
							}

							if (
								currentFormData?.team_assessment?.orange_hats &&
								!currentFormData?.team_assessment?.orange_hat_list?.length
							) {
								isValid = false;
							}

							if (isValid) {
								isValid =
									currentFormData?.crew?.length === projectInfoCrew?.size &&
									currentFormData?.crew?.reduce(
										(
											acc,
											{
												name = '',
												employee_crew_member_name = '',
												non_employee_crew_member_name = '',
												crew_member_name = '',
												signature
											}
										) =>
											!acc
												? acc
												: Boolean(
														projectInfoCrew?.crew.has(
															name ||
																employee_crew_member_name ||
																non_employee_crew_member_name ||
																crew_member_name
														) && signature
													),
										true
									);
							}

							break;
						case 'hazardous_energy_loto':
							// formData is an array for packet's created with older app versions with newer backend deployed
							isValid =
								Array.isArray(currentFormData) ||
								(!Object.keys(currentFormData?.concerns || '{}').filter(
									concernSection =>
										Object.values(
											currentFormData?.concerns?.[concernSection]?.fields || '{}'
										)?.filter(concern => concern.yes && !concern.verified).length
								).length &&
									currentFormData?.supervisor_name &&
									currentFormData?.supervisor_signature);

							if (tab === 'unassigned') {
								isValid &&=
									currentFormData?.project_manager_name && currentFormData?.project_manager_signature;
							}

							break;
						case 'delta_p_list':
							// formData is an array for packet's created with older app versions with newer backend deployed
							isValid =
								Array.isArray(currentFormData) ||
								(currentFormData?.complete &&
									currentFormData?.mitigation_plan?.delta_p_hazards_site_representatives &&
									currentFormData?.mitigation_plan?.lockout_tagout_plan &&
									currentFormData?.mitigation_plan?.plan_discussed);

							break;
						case 'delta_p_field_list':
							// formData is an array for packet's created with older app versions with newer backend deployed
							isValid =
								Array.isArray(currentFormData) ||
								((currentFormData?.delta_p_hazards?.no || currentFormData?.delta_p_hazards?.yes) &&
									(!currentFormData?.original_plan_modified?.yes ||
										currentFormData?.location_changed_reason) &&
									// if delta_p_hazards is no, then check delta_p_hazards_acknowledged,
									// otherwise require structure_layout_delta_p and capture_zone_reviewed
									(currentFormData?.delta_p_hazards?.no
										? currentFormData?.delta_p_hazards_acknowledged
										: currentFormData?.structure_layout_delta_p &&
											currentFormData?.capture_zone_reviewed &&
											currentFormData?.crew?.length === projectInfoCrew?.size) &&
									currentFormData?.crew?.reduce(
										(
											acc,
											{
												name = '',
												employee_crew_member_name = '',
												non_employee_crew_member_name = '',
												crew_member_name = '',
												signature
											}
										) =>
											!acc
												? acc
												: Boolean(
														projectInfoCrew?.crew.has(
															name ||
																employee_crew_member_name ||
																non_employee_crew_member_name ||
																crew_member_name
														) && signature
													),
										true
									));

							break;
						case 'general_daily_toolbox': {
							let _inited = false;

							if (Object.keys(currentFormData || {}).length <= 0) {
								currentFormData = {
									hazards: {},
									protective_equipment: {},
									emergency_procedures: {},
									team_assessment: {},
									crew: []
								};
							} else {
								_inited = true;
							}

							if (
								currentFormData?.todays_tasks === '' ||
								currentFormData?.hazards?.known_hazards === ''
							) {
								isValid = false;
							}

							if (
								currentFormData?.team_assessment?.orange_hats &&
								!currentFormData?.team_assessment?.orange_hat_list?.length
							) {
								isValid = false;
							}

							if (isValid) {
								isValid =
									currentFormData?.crew?.length === projectInfoCrew?.size &&
									currentFormData?.crew?.reduce(
										(
											acc,
											{
												name = '',
												employee_crew_member_name = '',
												non_employee_crew_member_name = '',
												crew_member_name = '',
												signature
											}
										) =>
											!acc
												? acc
												: Boolean(
														projectInfoCrew?.crew.has(
															name ||
																employee_crew_member_name ||
																non_employee_crew_member_name ||
																crew_member_name
														) && signature
													),
										true
									);
							}

							if (!_inited) {
								isValid = false;
							}

							break;
						}
						case 'dive_log':
							isValid = Object.values(currentFormData || {}).reduce((acc, diveLog) => {
								if (acc === false) {
									return acc;
								}

								let _valid = true;

								if (
									diveLog.data.supervisor === '' ||
									diveLog.data.diver_bailout_bottle === '' ||
									diveLog.data.standby_diver === '' ||
									diveLog.data.standby_diver_bailout_bottle === '' ||
									diveLog.data.tender === '' ||
									(diveLog.data.surface_interval_from_previous_dive.length > 0 &&
										diveLog.data.surface_interval_from_previous_dive.length < 4) ||
									diveLog.data.repetitive_group === '' ||
									diveLog.data.repetitive_group === null ||
									diveLog.data.residual_nitrogen_time === '' ||
									diveLog.data.type_of_dive === '' ||
									diveLog.data.visibility === '' ||
									diveLog.data.current === '' ||
									diveLog.data.air_temp === '' ||
									diveLog.data.water_temp === '' ||
									diveLog.data.bottom_condition === '' ||
									diveLog.data.thermal_protection_used === '' ||
									diveLog.data.times.leave_surface.length < 4 ||
									diveLog.data.times.leave_surface > 2400 ||
									diveLog.data.times.reach_bottom.length < 4 ||
									diveLog.data.times.reach_bottom > 2400 ||
									diveLog.data.times.leave_bottom.length < 4 ||
									diveLog.data.times.leave_bottom > 2400 ||
									diveLog.data.times.reach_surface.length < 4 ||
									diveLog.data.times.reach_surface > 2400 ||
									diveLog.data.max_depth === '' ||
									diveLog.data.table_schedule === '' ||
									diveLog.data.group === '' ||
									diveLog.data.group === null ||
									diveLog.data.max_bottom_time === '' ||
									diveLog.data.tasks_completed === ''
								) {
									_valid = false;
								}

								if (
									!diveLog.data.divers_condition_upon_surfacing_ok &&
									diveLog.data.divers_condition_details === ''
								) {
									_valid = false;
								}

								if (
									!diveLog.data.diver ||
									!diveLog.data.diver_signature ||
									!diveLog.data.supervisor ||
									!diveLog.data.supervisor_signature
								) {
									_valid = false;
								}

								return _valid;
							}, true);

							break;
						default:
							break;
					}
				}

				acc[form.form_id] = isValid;
				return acc;
			}, {}) || {};

		const isPacketValid = Object.values(currentPacketValidation).every(validation => validation);

		return [currentPacketValidation, isPacketValid];
	}, [currentPacket?.packet_forms]);

	const [driverExemptionRecords, driverExemptionRecordOrder] = React.useMemo(() => {
		if (!supervisorPacketId || !packetList.length) {
			return [[], []];
		}

		const { driver_exemptions } = JSON.parse(
			packetList
				.find(({ packet_id }) => packet_id === supervisorPacketId)
				?.packet_forms?.find(packet_form => packet_form.form_id === 'supervisor_handoff').form_data || '{}'
		);

		const driverExemptionRecords = driver_exemptions
			? Object.values(driver_exemptions)
					.flat()
					.map(exemption => ({
						...exemption,
						title: `${currentPhase.project_number}-${currentPhase.phase}_${exemption.name}`
					}))
			: [];

		const driverExemptionRecordOrder = driverExemptionRecords
			.map((_, index) => index)
			.sort((indexA, indexB) =>
				driverExemptionRecords[indexA].timestamp > driverExemptionRecords[indexB].timestamp ? -1 : 1
			);

		return [driverExemptionRecords, driverExemptionRecordOrder];
	}, [supervisorPacketId, packetList]);

	const webforms = useSelector(state =>
		state.webforms.forms
			.filter(({ tags }) => tags.find(({ name }) => name === currentPacket?.packet_tag))
			.filter(({ id }) => {
				if (currentPacket?.packet_tag === 'supervisor') {
					return true;
				}

				const currentPacketForm = currentPacket?.packet_forms.find(({ form_id }) => form_id === id);

				if (currentPacketSubmitted) {
					return currentPacketForm;
				}

				// Custom filters for rendering forms in form menu side bar
				switch (id) {
					case 'hazardous_energy_loto':
					case 'delta_p_list': {
						return (
							Boolean(currentPacketForm) &&
							!Array.isArray(JSON.parse(currentPacketForm?.form_data || '[]'))
						);
					}
					default:
						return true;
				}
			})
			.sort((a, b) => {
				const orderA = a.tags.find(({ name }) => name === currentPacket?.packet_tag).order;
				const orderB = b.tags.find(({ name }) => name === currentPacket?.packet_tag).order;
				return orderA - orderB;
			})
			.map(({ uuid, id, title }) => ({
				uuid,
				id,
				title
			}))
	);

	React.useEffect(() => {
		const handlePopstate = () => {
			changeNavTab(PATH_TAB[window.location.pathname]);
		};

		window.addEventListener('popstate', handlePopstate);
		window.addEventListener('resize', resizeWindow);

		axios.defaults.headers['Cache-Control'] = 'no-cache';
		axios.defaults.headers['Pragma'] = 'no-cache';
		axios.defaults.headers['Expires'] = '0';

		refreshAuthTokens().then(({ token, csrf }) => {
			axios.defaults.headers.common.Authorization = `Bearer ${token}`;
			axios.defaults.headers.common['X-CSRF-TOKEN'] = csrf;
		});

		axios.interceptors.response.use(
			response => response,
			error => {
				if (error?.response?.status === 403) {
					axios.defaults.headers.common.Authorization = null;

					return refreshAuthTokens()
						.then(({ token, csrf }) => {
							if (token && csrf && error.config) {
								if (error.config) {
									error.config.headers.Authorization = 'Bearer ' + token;
									error.config.headers['X-CSRF-TOKEN'] = csrf;

									return Promise.resolve(axios(error.config));
								} else {
									axios.defaults.headers.common.Authorization = `Bearer ${token}`;
								}
							} else if (error.message !== 'Network Error') {
								localStorage.setItem('isLoggedIn', false);
								window.open('/user/logout');
							}
						})
						.catch(_error => {
							if (_error?.response?.status === 403 || _error?.response?.status === 400) {
								localStorage.setItem('isLoggedIn', false);
								window.open('/user/logout');
							} else if (_error.message !== 'Network Error') {
								Sentry.captureException(_error);
								return Promise.reject(error);
							}
						});
				}

				return Promise.reject(error);
			}
		);

		if (tab === 'unassigned') {
			getPackets()
				.then(packets => setPacketList(packets.reverse()))
				.catch(setError);
		}

		getWebforms()
			.then(forms => dispatch(loadWebforms(forms)))
			.catch(() =>
				setErrorDialog({
					header: 'Error Fetching Webforms',
					body: 'Please refresh to retry'
				})
			);

		return () => {
			window.removeEventListener('popstate', handlePopstate);
			window.removeEventListener('resize', resizeWindow);
		};
	}, []);

	React.useEffect(() => {
		if (variableSizeListRef.current) {
			navigationList.map((project, index) => variableSizeListRef.current.resetAfterIndex(index));
		}
	}, [
		currentProject,
		currentPhase,
		packetList,
		searchQuery,
		searchArchives,
		projectLevelFormsOpen,
		dailyPacketsListOpen,
		driverExemptionListOpen
	]);

	React.useEffect(() => {
		if (!editMode && saveChanges.length) {
			setConfirmChanges(true);
		}
	}, [editMode, saveChanges]);

	React.useEffect(() => {
		if (navEnabled && navDrawerEnabled && !navDrawerOpen && !currentForm) {
			setNavDrawerOpen(true);
		}
	}, [currentForm, tab]);

	const handleNavChange = nextNav => {
		if (saveChanges.length) {
			setNextNavigation(() => nextNav);
			setConfirmChanges(true);
		} else {
			setEditMode(false);

			if (
				(currentPacket?.packet_tag === 'supervisor' || tab === 'unassigned') &&
				currentFormId === 'hazardous_energy_loto' &&
				lotoData?.lockout_tagout_required?.yes &&
				(!lotoData?.project_manager_name || !lotoData?.project_manager_signature)
			) {
				setSignatureMissingDialogOpen(true);
			} else {
				nextNav();
			}
		}
	};

	const changeNavTab = tab => {
		if (tab === 'unassigned') {
			setPacketListLoading(true);

			getPackets()
				.then(packets => {
					setPacketListLoading(false);
					setPacketList(packets.reverse());
				})
				.catch(setError);
		}

		setSupervisorPacketId('');
		setCurrentPacketId('');
		setCurrentFormId('');
		setCurrentPhase({});
		setCurrentProject('');
		setSearchQuery('');
		setPacketList([]);
		setAssignPacket(false);
		setError(null);
		setTab(tab);
		setProjectLevelFormsOpen(false);
		setDailyPacketsListOpen(false);
		setDriverExemptionListOpen(false);
		setCurrentDriverExemption(null);
	};

	// Function for forcing re-render of FormEditor if currentFormId doesn't change
	const resetForm = async () => setCurrentFormId('');

	const queueFormChanges = data => {
		if (editMode) {
			setSaveChanges(prev => {
				// Find if form is already queue'd for a given packet
				const index = prev.findIndex(
					({ packetId, data: { form_id } }) =>
						packetId === currentPacket.packet_id && form_id === data.form_id
				);

				if (index >= 0) {
					// overwrite previously queue'd data for matching forms in matching packets
					prev[index] = {
						...prev[index],
						data: { ...data }
					};
					return prev;
				} else {
					return [
						...prev,
						{
							packetId: currentPacket.packet_id,
							data: { ...data }
						}
					];
				}
			});
		}
	};

	const getListHeight = index => {
		const [projectNumber, phaseList] = navigationList[index];

		const PROJECT_BASE_HEIGHT = 100;

		// selected project
		if (currentProject !== projectNumber) {
			return PROJECT_BASE_HEIGHT;
		}

		const PROJECT_PHASES_HEIGHT = 36 * phaseList.length;

		// selected phase
		if (!currentPhase.phase) {
			return PROJECT_BASE_HEIGHT + PROJECT_PHASES_HEIGHT;
		}

		const loadingSpinnerHeight = packetListLoading ? 24 : 0;
		const errorHeight = error ? 20 : 0;

		const phaseMenuHeight = !loadingSpinnerHeight && !errorHeight ? 3 * 40 : 0; // 40px each: Project Level Forms, Packets by Date & Driver Exemptions
		const projectLevelHeight = projectLevelFormsOpen ? 3 * 40 : 0; // 40px each: PI, LL & Delta-P Checklist

		const createDailyPacketButtonHeight = phaseMenuHeight ? 4 : 0;

		const PHASE_HEIGHT =
			loadingSpinnerHeight + errorHeight + phaseMenuHeight + projectLevelHeight + createDailyPacketButtonHeight;

		// driver exemptions list selected
		const driverExemptionsHeight =
			phaseMenuHeight && driverExemptionListOpen
				? driverExemptionRecords.length
					? 36 * driverExemptionRecords.length
					: 20
				: 0;

		const loadMoreHeight = packetList.length < currentPhase?.packets?.length ? 20 : 0;

		const packetsHeight = phaseMenuHeight && dailyPacketsListOpen ? dailyPacketsHeight + loadMoreHeight : 0;

		return PROJECT_BASE_HEIGHT + PROJECT_PHASES_HEIGHT + PHASE_HEIGHT + driverExemptionsHeight + packetsHeight;
	};

	// NOTE: data variable is either updatedForm for packets or
	// an object with type and iD if remove is true,
	// or attachment data if not for attachments.
	const saveForm = (data, attachment = false, remove = false) => {
		setPacketList(prevPacketList =>
			prevPacketList.map(packet => ({
				...packet,
				changed: moment().unix().toString(),
				packet_forms: !attachment
					? packet.packet_forms.map(form =>
							packet.packet_id === currentPacketId && form.id === data.id
								? {
										...data,
										form_data: JSON.stringify(data.form_data)
									}
								: form
						)
					: packet.packet_forms,
				packet_attachments:
					attachment && packet.packet_id === currentPacketId
						? {
								...packet.packet_attachments,
								[data.type]: remove
									? packet.packet_attachments[data.type].filter(({ id }) => id !== data.id)
									: [...packet.packet_attachments[data.type], data]
							}
						: packet.packet_attachments
			}))
		);

		if (!attachment) {
			dbounce(() =>
				savePacketForm(currentPacket.packet_id, data).catch(error => {
					setErrorDialog({
						header: 'Error Saving Packet',
						body: error.message
					});
				})
			);
		}
	};

	const handleExport = () => {
		const url = '/generatepdf' + ('?packet=' + currentPacket.packet_nid);

		window.open(window.location.origin + url, '_blank');
	};

	const handleAssign = () => {
		assignPacketToProject({
			packetId: currentPacketId,
			projectNumber: assignTo.projectNumber,
			phaseNumber: assignTo.phaseNumber,
			packetNid: currentPacket.packet_nid,
			packetCreated: currentPacket.created
		})
			.then(() => {
				if (currentPhase?.packets) {
					sendReassignmentConfirmation({
						uid: localStorage.getItem('uid'),
						reassignedPacket: currentPacket.packet_nid,
						from: `${currentPhase.project_number}-${currentPhase.phase}`,
						to: `${assignTo.projectNumber}-${assignTo.phaseNumber}`
					});
					setCurrentPhase(phase => ({
						...phase,
						packets: phase.packets.filter(packet => packet.packet_id !== currentPacketId)
					}));
				}

				const piForm = currentPacket.packet_forms.find(form => form.form_id === 'supervisor_handoff');
				const piFormData = JSON.parse(piForm.form_data);

				piFormData.project_number = assignTo.projectNumber;
				piFormData.phase_number = assignTo.phaseNumber;
				piFormData.project_name = assignTo.projectName;

				const updatedForm = { ...piForm, form_data: piFormData };
				savePacketForm(currentPacketId, updatedForm)
					.then(() => {
						setPacketList(prevPacketList =>
							prevPacketList.filter(packet => packet.packet_id !== currentPacketId)
						);
						setAssignPacket(false);
						setSearchQuery('');
						setAssignTo({});
						setAssignConfirmation(false);
					})
					.catch(error => {
						setAssignPacket(false);
						setAssignTo({});
						setAssignConfirmation(false);
						setErrorDialog({
							header: 'Error Saving Packet',
							body: error.message
						});
					});
			})
			.catch(error => {
				setErrorDialog({
					header: currentPhase ? 'Error Reassigning Packet' : 'Error Assigning Packet',
					body: error.message
				});
			});
	};

	const Form = ({ form_id, required, title }) => (
		<List.Item mb="4px">
			<Link
				_focus={{
					boxShadow: '0 0 0',
					textDecoration: 'none'
				}}
				_hover={{ textDecoration: 'none' }}
				display="block"
				p="2px"
				fontWeight={form_id === currentFormId ? 'bold' : 'normal'}
				color={required ? 'black' : theme.colors.gray[600]}
				href="#"
				onClick={e => {
					e.preventDefault();

					handleNavChange(() => {
						const form = currentPacket.packet_forms
							.map(({ form_id: _form_id }) => _form_id)
							.find(_form_id => _form_id === form_id);

						if (!form) {
							createNewForm({
								dataAPI,
								tag: currentPacket.packet_tag,
								formId: form_id,
								packetId: currentPacketId,
								packetNid: currentPacket.packet_nid
							})
								.then(newForm => {
									setPacketList(prevPacketList =>
										prevPacketList.map(packet => ({
											...packet,
											packet_forms:
												packet.packet_id === currentPacketId
													? [...packet.packet_forms, newForm]
													: packet.packet_forms
										}))
									);

									setCurrentFormId(form_id);
									setPacketOptionsDialog(false);
									setFormOptionsDialog(false);
									setAttachmentsIndex(null);
								})
								.catch(error => {
									setErrorDialog({
										header: 'Error Creating New Form',
										body: error.message
									});
								});
						} else {
							setCurrentFormId(form_id);
							setPacketOptionsDialog(false);
							setFormOptionsDialog(false);
							setAttachmentsIndex(null);
						}
					});
				}}
			>
				<List.Indicator asChild>
					{!required || currentPacketValidation?.[form_id] ? (
						<ChakraIcon boxSize={5} color={required ? 'green.500' : theme.colors.gray[600]}>
							<FaCheckCircle />
						</ChakraIcon>
					) : (
						<ChakraIcon boxSize={5} color={required ? 'yellow.500' : theme.colors.gray[600]}>
							<FaExclamationCircle />
						</ChakraIcon>
					)}
				</List.Indicator>
				{title}
			</Link>
		</List.Item>
	);

	const AssignPacketList = ({ index, style }) => {
		const { project_number, phase_number, phase_name, project_name } = navigationList[index];

		return (
			<Flex style={style} flexDirection="column">
				<Flex flex={1} mx={5} position="relative" flexDirection="column" justifyContent="center">
					<HStack spacing={0}>
						<Link
							pb={2}
							href="#"
							onClick={async e => {
								e.preventDefault();
								setAssignTo({
									projectNumber: project_number,
									phaseNumber: phase_number,
									phaseName: phase_name,
									projectName: project_name
								});
							}}
							fontWeight={
								assignTo?.projectNumber === project_number && assignTo?.phaseNumber === phase_number
									? 'bold'
									: 'normal'
							}
						>
							<Box>
								{phase_name} - {project_name}
							</Box>
							<Box>
								{project_number}-{phase_number}
							</Box>
						</Link>
					</HStack>
				</Flex>
				<Separator bgColor={theme.colors.white} height="3px" />
			</Flex>
		);
	};

	const FormOptionsDialog = () => (
		<VStack alignSelf="flex-end" spacing="12px" p="12px" rounded="base" bg={theme.colors.white} boxShadow="dark-lg">
			{tab !== 'project-forms' || currentPacket.packet_tag !== 'supervisor' ? (
				<HStack w="100%" spacing={2}>
					{Object.keys(currentPacket.packet_attachments).map((label, index) => (
						<Button
							key={index}
							size="xs"
							color={theme.colors.white}
							bg={theme.colors.blue[700]}
							_hover={{ backgroundColor: theme.colors.blue[800] }}
							variant="solid"
							textTransform="capitalize"
							onClick={() =>
								handleNavChange(() => {
									setAttachmentsIndex(index);
									setFormOptionsDialog(false);
									setCurrentFormId('');
								})
							}
						>
							{(label === 'comments' ? 'notes' : label) +
								` (${currentPacket.packet_attachments?.[label]?.length || 0})`}
						</Button>
					))}
				</HStack>
			) : null}
			<List.Root w="100%" bg={theme.colors.white} borderRadius="2px">
				{webforms.map(form => (
					<Form key={form.uuid} form_id={form.id} required={!notReq.has(form.id)} title={form.title} />
				))}
			</List.Root>
			{currentPacket?.unassigned === 'true' ? (
				<Button
					size="xs"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					variant="solid"
					onClick={() => setAssignPacket(true)}
				>
					Assign Packet
				</Button>
			) : currentPacket.packet_tag === 'supervisor' ? (
				<Button
					size="xs"
					disabled={!isPacketValid || currentPacketSubmitted}
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					variant="solid"
					onClick={handleExport}
				>
					Export Forms
				</Button>
			) : (
				<>
					<HStack w="100%" spacing={2}>
						<Dialog.Root
							role="alertdialog"
							initialFocusEl={negativeTimeLogRef}
							onOpenChange={({ open }) => {
								if (!open) {
									setNegativeTimeLogAlert(false);
								}
							}}
							open={negativeTimeLogAlert}
							placement="center"
						>
							<Portal>
								<Dialog.Backdrop />
								<Dialog.Positioner>
									<Dialog.Content opacity="1 !important">
										<Dialog.Header>
											<Dialog.Title fontSize="lg" fontWeight="bold">
												Negative Time Exists
											</Dialog.Title>
										</Dialog.Header>
										<Dialog.Body>
											<p>Please enter leave time on the Daily Field Record before submitting.</p>
										</Dialog.Body>
										<Dialog.Footer>
											<Dialog.ActionTrigger asChild>
												<Button
													ref={negativeTimeLogRef}
													ml={3}
													color={theme.colors.white}
													bg={theme.colors.blue[700]}
													_hover={{ backgroundColor: theme.colors.blue[800] }}
													onClick={() => setNegativeTimeLogAlert(false)}
												>
													Okay
												</Button>
											</Dialog.ActionTrigger>
										</Dialog.Footer>
										<Dialog.CloseTrigger asChild>
											<CloseButton size="sm" />
										</Dialog.CloseTrigger>
									</Dialog.Content>
								</Dialog.Positioner>
							</Portal>
						</Dialog.Root>
						<Dialog.Root
							role="alertdialog"
							initialFocusEl={submitPacketRef}
							onOpenChange={({ open }) => {
								if (!open && !submitting) {
									setSubmitConfirmation(false);
								}
							}}
							open={submitConfirmation}
							placement="center"
						>
							<Portal>
								<Dialog.Backdrop />
								<Dialog.Positioner>
									<Dialog.Content opacity="1 !important">
										<Dialog.Header>
											<Dialog.Title fontSize="lg" fontWeight="bold">
												Confirm Submission
											</Dialog.Title>
										</Dialog.Header>
										<Dialog.Body>
											<p>Are you sure you want to submit this packet?</p>
										</Dialog.Body>
										<Dialog.Footer>
											<Dialog.ActionTrigger asChild>
												<Button
													colorPalette="red"
													ref={submitPacketRef}
													color={theme.colors.white}
													bg={theme.colors.red[500]}
													_hover={{ background: theme.colors.red[600] }}
													onClick={() => setSubmitConfirmation(false)}
													disabled={submitting}
												>
													Cancel
												</Button>
											</Dialog.ActionTrigger>
											<Button
												w="87px"
												ml={3}
												color={theme.colors.white}
												bg={theme.colors.blue[700]}
												_hover={{ backgroundColor: theme.colors.blue[800] }}
												onClick={async () => {
													setSubmitting(true);

													await submitPacket(currentPacketId)
														.then(id => {
															setPacketList(prev =>
																prev.map(packet => {
																	if (packet.packet_id === id) {
																		packet.submitted = true;
																	}

																	return packet;
																})
															);
															setSubmissionStatus(true);
														})
														.catch(() => setSubmissionStatus(false));

													setSubmitting(false);
													setSubmitConfirmation(false);
												}}
												disabled={submitting}
											>
												{submitting ? <Spinner p="16px" /> : 'Submit'}
											</Button>
										</Dialog.Footer>
										<Dialog.CloseTrigger asChild>
											<CloseButton size="sm" />
										</Dialog.CloseTrigger>
									</Dialog.Content>
								</Dialog.Positioner>
							</Portal>
						</Dialog.Root>
						<Dialog.Root
							role="alertdialog"
							initialFocusEl={submissionStatusRef}
							onOpenChange={({ open }) => {
								if (!open) {
									setSubmissionStatus(null);
								}
							}}
							open={typeof submissionStatus === 'boolean'}
							placement="center"
						>
							<Portal>
								<Dialog.Backdrop />
								<Dialog.Positioner>
									<Dialog.Content opacity="1 !important">
										<Dialog.Header>
											<Dialog.Title fontSize="lg" fontWeight="bold">
												{submissionStatus ? 'Packet Submitted' : 'Failed to Submit Packet'}
											</Dialog.Title>
										</Dialog.Header>
										<Dialog.Body>
											<p>
												{submissionStatus
													? 'Your packet has been successfully submitted!'
													: 'Could not submit packet. Please try again later.'}
											</p>
										</Dialog.Body>
										<Dialog.Footer>
											<Dialog.ActionTrigger asChild>
												<Button
													ref={submissionStatusRef}
													ml={3}
													color={theme.colors.white}
													bg={theme.colors.blue[700]}
													_hover={{ backgroundColor: theme.colors.blue[800] }}
													onClick={() => setSubmissionStatus(null)}
												>
													Okay
												</Button>
											</Dialog.ActionTrigger>
										</Dialog.Footer>
										<Dialog.CloseTrigger asChild>
											<CloseButton size="sm" />
										</Dialog.CloseTrigger>
									</Dialog.Content>
								</Dialog.Positioner>
							</Portal>
						</Dialog.Root>
						<Button
							size="xs"
							disabled={!isPacketValid || currentPacketSubmitted}
							color={theme.colors.white}
							bg={theme.colors.blue[700]}
							_hover={{ backgroundColor: theme.colors.blue[800] }}
							variant="solid"
							onClick={() => {
								let dfrTimeLogsValid = Boolean(currentPacket);

								if (dfrTimeLogsValid) {
									const dfr = dataAPI.getFormData('daily_field_record');
									dfrTimeLogsValid = dfr
										? !dfr.crew_members?.some(({ timelogs }) =>
												Object.values(timelogs).some(timelog => timelog?.hours < 0)
											)
										: true;
								}

								if (dfrTimeLogsValid) {
									setSubmitConfirmation(true);
								} else {
									setNegativeTimeLogAlert(true);
								}
							}}
						>
							{window.canEditSubmittedPackets ? 'Submit Packet' : 'Submit Completed Packet'}
						</Button>
						{window.canEditSubmittedPackets && (
							<Button
								disabled={!isPacketValid}
								size="xs"
								color={theme.colors.white}
								bg={theme.colors.blue[700]}
								_hover={{ backgroundColor: theme.colors.blue[800] }}
								variant="solid"
								onClick={handleExport}
							>
								Export Forms
							</Button>
						)}
					</HStack>
					<HStack w="100%" spacing={2}>
						<Button
							size="xs"
							color={theme.colors.white}
							bg={theme.colors.blue[700]}
							_hover={{ backgroundColor: theme.colors.blue[800] }}
							variant="solid"
							onClick={() =>
								handleNavChange(() =>
									createNewPacket(dataAPI, 'packet', packetList, currentPhase, currentPacket)
										.then(async newPacket => {
											await resetForm();
											return newPacket;
										})
										.then(newPacket => {
											setPacketList(prevPacketList => [newPacket, ...prevPacketList]);
											setCurrentPacketId(newPacket.packet_id);
											setCurrentFormId('supervisor_handoff');
											setFormOptionsDialog(false);
										})
										.catch(error =>
											setErrorDialog({
												header: 'Error Creating New Packet',
												body: error.message
											})
										)
								)
							}
						>
							Duplicate
						</Button>
						<Button
							size="xs"
							color={theme.colors.white}
							bg={theme.colors.blue[700]}
							_hover={{ backgroundColor: theme.colors.blue[800] }}
							variant="solid"
							onClick={() => setCalendar(prevCalendar => !prevCalendar)}
						>
							Change Date
						</Button>
						{window.canEditSubmittedPackets ? (
							<Button
								size="xs"
								color={theme.colors.white}
								bg={theme.colors.blue[700]}
								_hover={{ backgroundColor: theme.colors.blue[800] }}
								variant="solid"
								onClick={() => setAssignPacket(true)}
							>
								Reassign
							</Button>
						) : (
							<Button
								disabled={!isPacketValid}
								size="xs"
								color={theme.colors.white}
								bg={theme.colors.blue[700]}
								_hover={{ backgroundColor: theme.colors.blue[800] }}
								variant="solid"
								onClick={handleExport}
							>
								Export Forms
							</Button>
						)}
					</HStack>
				</>
			)}
		</VStack>
	);

	const ExportForms = () => (
		<>
			<Spacer
				w="100%"
				mx="8px !important"
				my="auto !important"
				borderBottom={`2px dotted ${theme.colors.gray[600]}`}
			/>
			<Button
				onClick={handleExport}
				size="xs"
				color={theme.colors.white}
				bg={theme.colors.blue[700]}
				_hover={{ backgroundColor: theme.colors.blue[800] }}
				variant="solid"
			>
				Export Forms
			</Button>
		</>
	);

	const SupervisorPacket = ({ isCurrentPacket }) => (
		<>
			<List.Item key="dailyPackets" py="8px" h="40px">
				<HStack spacing={0}>
					<Link
						href="#"
						_focus={{
							boxShadow: '0 0 0',
							textDecoration: 'none'
						}}
						_hover={{ textDecoration: 'none' }}
						fontWeight={projectLevelFormsOpen ? 'bold' : 'normal'}
						onClick={e => {
							e.preventDefault();
							setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
							setPacketOptionsDialog(false);
							setProjectLevelFormsOpen(prevState => !prevState);
							setDailyPacketsListOpen(false);
							setDriverExemptionListOpen(false);
						}}
					>
						<Text>Project Level Forms</Text>
					</Link>
				</HStack>
			</List.Item>
			{projectLevelFormsOpen && (
				<List.Root ml="24px">
					<List.Item key={`${supervisorPacketId ?? 'top'}-supervisor_handoff`} py="8px" h="40px">
						<HStack spacing={0}>
							{supervisorPacketId ? (
								<Link
									href="#"
									fontWeight={
										isCurrentPacket && currentFormId === 'supervisor_handoff' ? 'bold' : 'normal'
									}
									_hover={{ textDecoration: 'none' }}
									_focus={{
										boxShadow: '0 0 0',
										textDecoration: 'none'
									}}
									onClick={e => {
										e.preventDefault();

										handleNavChange(() =>
											resetForm().then(() => {
												setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
												setPacketOptionsDialog(false);
												setCurrentPacketId(supervisorPacketId);
												setCurrentFormId('supervisor_handoff');
												setCurrentDriverExemption(null);
												setAttachmentsIndex(null);
											})
										);
									}}
								>
									<Text>Project Information</Text>
								</Link>
							) : (
								<Text>Project Information</Text>
							)}
							{isCurrentPacket && currentFormId === 'supervisor_handoff' ? (
								<ExportForms />
							) : (
								!supervisorPacketId && (
									<>
										<Spacer
											w="100%"
											mx="8px !important"
											my="auto !important"
											borderBottom={`2px dotted ${theme.colors.gray[600]}`}
										/>
										<Button
											onClick={() =>
												handleNavChange(() =>
													createNewPacket(dataAPI, 'supervisor', packetList, currentPhase)
														.then(async newPacket => {
															await resetForm();
															return newPacket;
														})
														.then(newPacket => {
															setPacketList(prevPacketList => [
																newPacket,
																...prevPacketList
															]);
															setCurrentPacketId(newPacket.packet_id);
															setSupervisorPacketId(newPacket.packet_id);
															setCurrentFormId('supervisor_handoff');
															setFormOptionsDialog(false);
															setAttachmentsIndex(null);
														})
														.catch(error => {
															setErrorDialog({
																header: 'Error Creating New Packet',
																body: error.message
															});
														})
												)
											}
											size="xs"
											color={theme.colors.white}
											bg={theme.colors.blue[700]}
											_hover={{ backgroundColor: theme.colors.blue[800] }}
											variant="solid"
										>
											Create Project Information
										</Button>
									</>
								)
							)}
						</HStack>
					</List.Item>
					<List.Item key={`${supervisorPacketId ?? 'top'}-loadout_list`} py="8px" h="40px">
						<HStack spacing={0}>
							{supervisorPacketId ? (
								<Link
									href="#"
									_focus={{
										boxShadow: '0 0 0',
										textDecoration: 'none'
									}}
									_hover={{ textDecoration: 'none' }}
									fontWeight={isCurrentPacket && currentFormId === 'loadout_list' ? 'bold' : 'normal'}
									onClick={e => {
										e.preventDefault();

										handleNavChange(() =>
											resetForm().then(() => {
												setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
												setPacketOptionsDialog(false);
												setCurrentPacketId(supervisorPacketId);
												setCurrentFormId('loadout_list');
												setCurrentDriverExemption(null);
												setAttachmentsIndex(null);
											})
										);
									}}
								>
									<Text>Loadout List</Text>
								</Link>
							) : (
								<Text>Loadout List</Text>
							)}
							{isCurrentPacket && currentFormId === 'loadout_list' && <ExportForms />}
						</HStack>
					</List.Item>
					<List.Item key={`${supervisorPacketId ?? 'top'}-hazardous_energy_loto`} py="8px" h="40px">
						<HStack spacing={0}>
							{supervisorPacketId && supervisorPacketFormsList?.includes('hazardous_energy_loto') ? (
								<Link
									href="#"
									_focus={{
										boxShadow: '0 0 0',
										textDecoration: 'none'
									}}
									_hover={{ textDecoration: 'none' }}
									fontWeight={
										isCurrentPacket && currentFormId === 'hazardous_energy_loto' ? 'bold' : 'normal'
									}
									onClick={e => {
										e.preventDefault();

										handleNavChange(() =>
											resetForm().then(() => {
												setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
												setPacketOptionsDialog(false);
												setCurrentPacketId(supervisorPacketId);
												setCurrentFormId('hazardous_energy_loto');
												setCurrentDriverExemption(null);
												setAttachmentsIndex(null);
											})
										);
									}}
								>
									<Text>Hazardous Energy LOTO</Text>
								</Link>
							) : (
								<Text>Hazardous Energy LOTO</Text>
							)}
							{isCurrentPacket && currentFormId === 'hazardous_energy_loto' ? (
								<ExportForms />
							) : (
								supervisorPacketId &&
								!supervisorPacketFormsList?.includes('hazardous_energy_loto') && (
									<>
										<Spacer
											w="100%"
											mx="8px !important"
											my="auto !important"
											borderBottom={`2px dotted ${theme.colors.gray[600]}`}
										/>
										<Button
											onClick={() =>
												handleNavChange(() => {
													const packetNid = packetList.find(
														({ packet_id }) => packet_id === supervisorPacketId
													)?.packet_nid;

													//Note: *should* always be true here
													if (packetNid) {
														createNewForm({
															dataAPI,
															tag: 'supervisor',
															formId: 'hazardous_energy_loto',
															packetId: supervisorPacketId,
															packetNid
														})
															.then(async newForm => {
																await resetForm();
																return newForm;
															})
															.then(newForm => {
																setPacketList(prevPacketList =>
																	prevPacketList.map(packet => ({
																		...packet,
																		packet_forms:
																			packet.packet_id === supervisorPacketId
																				? [...packet.packet_forms, newForm]
																				: packet.packet_forms
																	}))
																);

																setCurrentPacketId(supervisorPacketId);
																setCurrentFormId('hazardous_energy_loto');
																setFormOptionsDialog(false);
																setAttachmentsIndex(null);
															})
															.catch(error => {
																setErrorDialog({
																	header: 'Error Creating New Form',
																	body: error.message
																});
															});
													}
												})
											}
											size="xs"
											color={theme.colors.white}
											bg={theme.colors.blue[700]}
											_hover={{ backgroundColor: theme.colors.blue[800] }}
											variant="solid"
										>
											Create LOTO Form
										</Button>
									</>
								)
							)}
						</HStack>
					</List.Item>
					<List.Item key={`${supervisorPacketId ?? 'top'}-delta_p_list`} py="8px" h="40px">
						<HStack spacing={0}>
							{supervisorPacketId && supervisorPacketFormsList.includes('delta_p_list') ? (
								<Link
									href="#"
									_focus={{
										boxShadow: '0 0 0',
										textDecoration: 'none'
									}}
									_hover={{ textDecoration: 'none' }}
									fontWeight={isCurrentPacket && currentFormId === 'delta_p_list' ? 'bold' : 'normal'}
									onClick={e => {
										e.preventDefault();

										handleNavChange(() =>
											resetForm().then(() => {
												setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
												setPacketOptionsDialog(false);
												setCurrentPacketId(supervisorPacketId);
												setCurrentFormId('delta_p_list');
												setCurrentDriverExemption(null);
												setAttachmentsIndex(null);
											})
										);
									}}
								>
									<Text>Delta-P Checklist</Text>
								</Link>
							) : (
								<Text>Delta-P Checklist</Text>
							)}
							{isCurrentPacket && currentFormId === 'delta_p_list' ? (
								<ExportForms />
							) : (
								supervisorPacketId &&
								!supervisorPacketFormsList.includes('delta_p_list') && (
									<>
										<Spacer
											w="100%"
											mx="8px !important"
											my="auto !important"
											borderBottom={`2px dotted ${theme.colors.gray[600]}`}
										/>
										<Button
											onClick={() =>
												handleNavChange(() => {
													const packetNid = packetList.find(
														({ packet_id }) => packet_id === supervisorPacketId
													)?.packet_nid;

													//Note: *should* always be true here
													if (packetNid) {
														createNewForm({
															dataAPI,
															tag: 'supervisor',
															formId: 'delta_p_list',
															packetId: supervisorPacketId,
															packetNid
														})
															.then(async newForm => {
																await resetForm();
																return newForm;
															})
															.then(newForm => {
																setPacketList(prevPacketList =>
																	prevPacketList.map(packet => ({
																		...packet,
																		packet_forms:
																			packet.packet_id === supervisorPacketId
																				? [...packet.packet_forms, newForm]
																				: packet.packet_forms
																	}))
																);

																setCurrentPacketId(supervisorPacketId);
																setCurrentFormId('delta_p_list');
																setFormOptionsDialog(false);
																setAttachmentsIndex(null);
															})
															.catch(error => {
																setErrorDialog({
																	header: 'Error Creating New Form',
																	body: error.message
																});
															});
													}
												})
											}
											size="xs"
											color={theme.colors.white}
											bg={theme.colors.blue[700]}
											_hover={{ backgroundColor: theme.colors.blue[800] }}
											variant="solid"
										>
											Create Delta-P Checklist
										</Button>
									</>
								)
							)}
						</HStack>
					</List.Item>
				</List.Root>
			)}
		</>
	);

	const DriverExemptions = () => (
		<>
			<List.Item key="driverExemptions" py="8px" h="40px">
				<HStack spacing={0}>
					<Link
						href="#"
						_focus={{
							boxShadow: '0 0 0',
							textDecoration: 'none'
						}}
						_hover={{ textDecoration: 'none' }}
						fontWeight={driverExemptionListOpen ? 'bold' : 'normal'}
						onClick={e => {
							e.preventDefault();
							setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
							setPacketOptionsDialog(false);
							setProjectLevelFormsOpen(false);
							setDailyPacketsListOpen(false);
							setDriverExemptionListOpen(prevState => !prevState);
						}}
					>
						<Text>Driver Exemptions</Text>
					</Link>
				</HStack>
			</List.Item>
			{driverExemptionListOpen && (
				<List.Root>
					{driverExemptionRecords.length ? (
						driverExemptionRecordOrder.map(index => {
							const record = driverExemptionRecords[index];

							return (
								<List.Item key={`${record.title}_${index}`} py="8px" ml="24px">
									<HStack spacing={0}>
										<Link
											href="#"
											_focus={{
												boxShadow: '0 0 0',
												textDecoration: 'none'
											}}
											_hover={{ textDecoration: 'none' }}
											fontWeight={currentDriverExemption === index ? 'bold' : 'normal'}
											onClick={e => {
												e.preventDefault();

												setCurrentPacketId('');
												setCurrentFormId('');
												setCurrentDriverExemption(index);
											}}
										>
											<Text>{record.title}</Text>
										</Link>
									</HStack>
								</List.Item>
							);
						})
					) : (
						<Text color="tomato" fontSize="xs" fontWeight="bold" ml="24px">
							This Project Phase has no driver exemptions
						</Text>
					)}
				</List.Root>
			)}
		</>
	);

	const Packet = ({ packet, isPacketSubmitted }) => (
		<List.Item key={packet.packet_id} py="8px">
			<HStack spacing={0}>
				<Link
					_focus={{
						boxShadow: '0 0 0',
						textDecoration: 'none'
					}}
					_hover={{ textDecoration: 'none' }}
					href="#"
					onClick={e => {
						e.preventDefault();

						handleNavChange(() =>
							resetForm().then(() => {
								setActiveLog('');
								setAttachmentsIndex(null);
								setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
								setPacketOptionsDialog(false);
								setCurrentPacketId(packet.packet_id);
								setCurrentFormId('supervisor_handoff');
								setCurrentDriverExemption(null);
							})
						);
					}}
					fontWeight={currentPacketId === packet.packet_id ? 'bold' : 'normal'}
				>
					<HStack>
						{isPacketSubmitted ? (
							<ChakraIcon boxSize={5} color="green.500">
								<FaCheckCircle />
							</ChakraIcon>
						) : (
							<ChakraIcon boxSize={5} color="yellow.500">
								<FaExclamationCircle />
							</ChakraIcon>
						)}
						<Text>{packet.title}</Text>
					</HStack>
				</Link>
				{currentPacketId === packet.packet_id && (
					<>
						<Spacer
							w="100%"
							mx="8px !important"
							my="auto !important"
							borderBottom={`2px dotted ${theme.colors.gray[600]}`}
						/>
						<Image
							w={34}
							src="/themes/custom/msiforms/src/assets/images/Icon-Ellipsis.svg"
							onClick={() => {
								setPacketOptionsDialog(false);
								setFormOptionsDialog(prevState => !prevState);
							}}
						/>
					</>
				)}
			</HStack>
			{currentPacketId === packet.packet_id && formOptionsDialog ? (
				<VStack position="absolute" top="auto" right="1.25rem" alignItems="flex-end" zIndex={5}>
					<FormOptionsDialog />
					{calendar && (
						<ChangePacketDate
							setCalendar={setCalendar}
							currentPacket={currentPacket}
							phaseKey={`${currentPhase.project_number}__${currentPhase.phase}`}
							setPacketList={setPacketList}
						/>
					)}
				</VStack>
			) : null}
		</List.Item>
	);

	const DailyPackets = ({ phaseNumber }) => (
		<>
			<List.Item key="dailyPackets" py="8px" h="40px">
				<HStack spacing={0}>
					<Link
						href="#"
						_focus={{
							boxShadow: '0 0 0',
							textDecoration: 'none'
						}}
						_hover={{ textDecoration: 'none' }}
						fontWeight={dailyPacketsListOpen ? 'bold' : 'normal'}
						onClick={e => {
							e.preventDefault();
							setFormOptionsDialog(prevState => prevState || packetOptionsDialog);
							setPacketOptionsDialog(false);
							setProjectLevelFormsOpen(false);
							setDailyPacketsListOpen(prevState => !prevState);
							setDriverExemptionListOpen(false);
						}}
					>
						<Text>Packets by Date</Text>
					</Link>
				</HStack>
			</List.Item>
			{dailyPacketsListOpen && (
				<List.Root>
					{dailyPacketsLength ? (
						<>
							{packetList
								.filter(({ packet_tag }) => packet_tag !== 'supervisor')
								.map((packet, index) => (
									<Packet key={index} packet={packet} isPacketSubmitted={isSubmitted(packet)} />
								))}
							{currentPhase?.phase === phaseNumber &&
							!packetListLoading &&
							currentPhase?.packets?.length > packetList.length ? (
								morePacketsLoading ? (
									<Spinner ml="60px" size="sm" />
								) : (
									<>
										{loadMoreError ? (
											<Text color="tomato" fontSize="xs" fontWeight="bold" ml="60px">
												{loadMoreError.message}
											</Text>
										) : null}
										<Text
											color="blue"
											fontSize="xs"
											fontWeight="bold"
											ml="60px"
											_hover={{ cursor: 'pointer' }}
											onClick={async () => {
												setMorePacketsLoading(true);

												// Packets are fetched at a max of 50
												const page = packetList.length / 50;

												const packets = await getPackets(
													currentProject,
													phaseNumber,
													page
												).catch(() => setLoadMoreError(new Error('Failed to load packets')));

												if (packets?.length >= 0) {
													setPacketList(prev => [...prev, ...packets]);
													setLoadMoreError(null);
												} else {
													setLoadMoreError(new Error('Failed to load packets'));
												}

												setMorePacketsLoading(false);
											}}
										>
											Load More
										</Text>
									</>
								)
							) : null}
						</>
					) : (
						<Text color="tomato" fontSize="xs" fontWeight="bold" ml="24px">
							This Project Phase has no available packets
						</Text>
					)}
				</List.Root>
			)}
		</>
	);

	const UnassignedPacket = ({ index, style }) => {
		const { packet_id, title } = packetList[index];

		return (
			<Flex style={style} flexDirection="column">
				<Flex flex={1} mx={5} position="relative" flexDirection="column" justifyContent="center">
					<HStack spacing={0}>
						<Link
							pb={2}
							href="#"
							onClick={e => {
								e.preventDefault();
								handleNavChange(() =>
									resetForm().then(() => {
										setActiveLog('');
										setCurrentPacketId(packet_id);
										setCurrentFormId('supervisor_handoff');
									})
								);
							}}
							fontWeight={currentPacketId === packet_id ? 'bold' : 'normal'}
						>
							{title}
						</Link>
						{currentPacketId === packet_id ? (
							<>
								<Spacer
									mx="8px !important"
									my="auto !important"
									borderBottom={`2px dotted ${theme.colors.gray[600]}`}
								/>
								<Image
									w={34}
									src="/themes/custom/msiforms/src/assets/images/Icon-Ellipsis.svg"
									onClick={() => {
										setPacketOptionsDialog(false);
										setFormOptionsDialog(prevState => !prevState);
									}}
								/>
							</>
						) : null}
					</HStack>
					{currentPacketId === packet_id && formOptionsDialog && (
						<Box position="absolute" right={0} top="calc(50% + .75rem)" zIndex={5}>
							<FormOptionsDialog />
						</Box>
					)}
				</Flex>
				<Separator bgColor={theme.colors.white} height="3px" />
			</Flex>
		);
	};

	const Phase = ({ phaseNumber, phaseName }) => (
		<List.Item ml="20px" py="8px">
			<HStack spacing={0}>
				<Link
					href="#"
					onClick={e => {
						e.preventDefault();

						handleNavChange(async () => {
							setActiveLog('');
							setPacketList([]);
							setCurrentPhase({});
							setProjectLevelFormsOpen(false);
							setDailyPacketsListOpen(false);
							setDriverExemptionListOpen(false);
							setCurrentDriverExemption(null);

							try {
								const phase = await getPhase(currentProject, phaseNumber);

								if (phase) {
									setCurrentPhase(phase);
									setPacketListLoading(true);
									const packets = await getPackets(currentProject, phaseNumber);

									if (packets?.length >= 0) {
										setPacketList(packets);

										if (packets.length) {
											let supervisorPacket = packets.find(
												({ packet_tag }) => packet_tag === 'supervisor'
											);

											// If supervisorPacket does not exist in first 50 packets
											if (!supervisorPacket && phase.packets.length > 50) {
												supervisorPacket = phase.packets.find(
													({ packet_tag }) => packet_tag === 'supervisor'
												);

												// Fetch supervisorPacket if exists in older packet
												if (supervisorPacket) {
													supervisorPacket = await getPacket(supervisorPacket.packet_id);
													packets.push(supervisorPacket);
												}
											}

											setCurrentPacketId(supervisorPacket?.packet_id ?? packets[0].packet_id);
											setSupervisorPacketId(supervisorPacket?.packet_id || '');
											setCurrentFormId('supervisor_handoff');
											setAttachmentsIndex(null);
										} else {
											setCurrentPacketId('');
											setSupervisorPacketId('');
										}

										setPacketListLoading(false);
										setError(null);
									} else {
										setError(new Error('Failed to load packets'));
									}
								} else {
									setError(new Error('Failed to load project phase'));
								}
							} catch (error) {
								setPacketListLoading(false);
								setError(error);
								setCurrentPhase({ phase: phaseNumber });
							}

							setPacketOptionsDialog(formOptionsDialog);
							setPacketListLoading(false);
							setFormOptionsDialog(false);
						});
					}}
					fontWeight={currentPhase?.phase === phaseNumber ? 'bold' : 'normal'}
				>
					{phaseNumber} - {phaseName}
				</Link>
				{currentPhase?.phase === phaseNumber && !error && !packetListLoading && (
					<>
						<Spacer
							mx="8px !important"
							my="auto !important"
							borderBottom={`2px dotted ${theme.colors.gray[600]}`}
						/>
						<Button
							onClick={() =>
								handleNavChange(() => {
									if (
										supervisorPacket &&
										lotoData?.lockout_tagout_required?.yes &&
										(!lotoData?.project_manager_name || !lotoData?.project_manager_signature)
									) {
										setSignatureDialogOpen(true);
									} else {
										createNewPacket(dataAPI, 'packet', packetList, currentPhase)
											.then(newPacket => {
												if (!dailyPacketsLength) {
													const pi = packetList
														.find(({ packet_id }) => packet_id === supervisorPacketId)
														?.packet_forms?.find(
															packet_form => packet_form.form_id === 'supervisor_handoff'
														);

													if (pi) {
														const piData = JSON.parse(pi.form_data || '{}');

														if (piData.utility_work) {
															piData.driver_exemptions = {};

															const timestamp = new Date().getTime();
															const driverExemptions = piData.employee_crew_members
																.filter(({ dot_driver }) => dot_driver)
																.map(({ employee_crew_member_name }) => {
																	const { name, email } = window.employeeList.find(
																		({ name }) => name === employee_crew_member_name
																	);

																	const exemption = {
																		name,
																		email,
																		est_start_date: piData.est_start_date,
																		est_end_date: piData.est_end_date,
																		marine_solutions_project_manager:
																			piData.marine_solutions_project_manager,
																		client_name: piData.client_name,
																		project_steps: piData.project_steps,
																		timestamp
																	};

																	piData.driver_exemptions[name] = [exemption];
																	return exemption;
																});

															emailDriverExemptions(
																localStorage.getItem('uid'),
																driverExemptions,
																currentProject + '-' + currentPhase?.phase
															);
															saveForm({ ...pi, form_data: piData });
														}
													}
												}

												resetForm().then(() => {
													setPacketList(prevPacketList => [newPacket, ...prevPacketList]);
													setActiveLog('');
													setCurrentPacketId(newPacket.packet_id);
													setCurrentFormId('supervisor_handoff');
													setFormOptionsDialog(false);
													setAttachmentsIndex(null);
													setProjectLevelFormsOpen(false);
													setDailyPacketsListOpen(true);
													setDriverExemptionListOpen(false);
													setCurrentDriverExemption(null);
												});
											})
											.catch(error => {
												setErrorDialog({
													header: 'Error Creating New Packet',
													body: error.message
												});
											});
									}
								})
							}
							size="xs"
							bg={theme.colors.blue[700]}
							_hover={{ backgroundColor: theme.colors.blue[800] }}
						>
							Create Daily Packet
						</Button>
					</>
				)}
			</HStack>
			{currentPhase?.phase === phaseNumber &&
				(packetListLoading ? (
					<Spinner p="8px" />
				) : error ? (
					<Text color="tomato" fontSize="xs" fontWeight="bold">
						{error.message}
					</Text>
				) : (
					<List.Root ml="24px" my="2px">
						<SupervisorPacket
							isCurrentPacket={
								supervisorPacketId && currentFormId && supervisorPacketId === currentPacketId
							}
						/>
						<DailyPackets phaseNumber={phaseNumber} />
						<DriverExemptions />
					</List.Root>
				))}
		</List.Item>
	);

	const Project = ({ index, style }) => {
		const [projectNumber, phaseList] = navigationList[index];

		return (
			<Flex style={style} flexDirection="column">
				<Flex flex={1} flexDirection="column" justifyContent="center">
					<Link
						mx={5}
						pb={2}
						href="#"
						onClick={e => {
							e.preventDefault();

							handleNavChange(() => {
								setCurrentProject(projectNumber);
								setCurrentPhase({});
								setPacketList([]);
								setCurrentPacketId('');
								setSupervisorPacketId('');
								setCurrentFormId('');
								setActiveLog('');
								setError(null);
								setProjectLevelFormsOpen(false);
								setDailyPacketsListOpen(false);
								setDriverExemptionListOpen(false);
								setCurrentDriverExemption(null);
								setAttachmentsIndex(null);

								addRecentProject(projectNumber);
							});
						}}
						fontWeight={currentProject === projectNumber ? 'bold' : 'normal'}
					>
						{projectNumber} - {phaseList[0]?.project_name || ''}
					</Link>
					{currentProject === projectNumber ? (
						<List.Root mx={5}>
							{phaseList.map(({ phase_number, phase_name }, index) => (
								<Phase key={index} phaseNumber={phase_number} phaseName={phase_name} />
							))}
						</List.Root>
					) : null}
				</Flex>
				<Separator bgColor={theme.colors.white} height="3px" />
			</Flex>
		);
	};

	// const SafetyInspectionsList = ({ index, style }) => {
	// 	const { title, id } = navigationList[index];
	//
	// 	return (
	// 		<Flex style={style} flexDirection="column">
	// 			<Flex flex={1} mx={5} position="relative" flexDirection="column" justifyContent="center">
	// 				<HStack spacing={0}>
	// 					<Link
	// 						flex="0 0 auto"
	// 						pb={2}
	// 						href="#"
	// 						onClick={e => {
	// 							e.preventDefault();
	// 							handleNavChange(() => setCurrentFormId(id));
	// 						}}
	// 						fontWeight={currentFormId === id ? 'bold' : 'normal'}
	// 					>
	// 						{title}
	// 					</Link>
	// 					<Spacer
	// 						flex="1 0 auto"
	// 						mx="8px !important"
	// 						my="auto !important"
	// 						borderBottom={`2px dotted ${theme.colors.gray[600]}`}
	// 					/>
	// 					<Button
	// 						flex="0 0 12%"
	// 						size="xs"
	// 						color={theme.colors.white}
	// 						bg={theme.colors.blue[700]}
	// 						_hover={{ backgroundColor: theme.colors.blue[800] }}
	// 						variant="solid"
	// 						onClick={() => setDeleteSafetyInspectionConfirmation({ [index]: true })}
	// 					>
	// 						Delete
	// 					</Button>
	// 					<Dialog.Root
	// 						role="alertdialog"
	// 						initialFocusEl={deleteSafetyInspectionRefs.map.get(index)}
	// 						onOpenChange={({ open }) => {
	// 							if (!open) {
	// 								setDeleteSafetyInspectionConfirmation({ [index]: false });
	// 							}
	// 						}}
	// 						open={deleteSafetyInspectionConfirmation?.[index] ?? false}
	// 						placement="center"
	// 					>
	// 						<Portal>
	// 							<Dialog.Backdrop />
	// 							<Dialog.Positioner>
	// 								<Dialog.Content opacity="1 !important">
	// 									<Dialog.Header>
	// 										<Dialog.Title fontSize="lg" fontWeight="bold">
	// 											Confirm Deletion
	// 										</Dialog.Title>
	// 									</Dialog.Header>
	// 									<Dialog.Body>
	// 										<p>Are you sure you want to delete this Safety Inspection?</p>
	// 									</Dialog.Body>
	// 									<Dialog.Footer>
	// 										<Dialog.ActionTrigger asChild>
	// 											<Button
	// 												colorPalette="red"
	// 												ref={deleteSafetyInspectionRefs.ref(index)}
	// 												color={theme.colors.white}
	// 												bg={theme.colors.red[500]}
	// 												_hover={{ background: theme.colors.red[600] }}
	// 												onClick={() => setDeleteSafetyInspectionConfirmation(false)}
	// 											>
	// 												Cancel
	// 											</Button>
	// 										</Dialog.ActionTrigger>
	// 										<Button
	// 											colorPalette="red"
	// 											w="87px"
	// 											ml={3}
	// 											color={theme.colors.white}
	// 											bg={theme.colors.blue[700]}
	// 											_hover={{ backgroundColor: theme.colors.blue[800] }}
	// 											onClick={() => {
	// 												setDeleteSafetyInspectionConfirmation(false);
	// 												dispatch(deleteSafetyInspectionForm(id));
	// 											}}
	// 										>
	// 											Delete
	// 										</Button>
	// 									</Dialog.Footer>
	// 									<Dialog.CloseTrigger asChild>
	// 										<CloseButton size="sm" />
	// 									</Dialog.CloseTrigger>
	// 								</Dialog.Content>
	// 							</Dialog.Positioner>
	// 						</Portal>
	// 					</Dialog.Root>
	// 				</HStack>
	// 			</Flex>
	// 			<Separator bgColor={theme.colors.white} height="3px" />
	// 		</Flex>
	// 	);
	// };

	return (
		<Flex minWidth={['0px', '0px', '700px']} width="100%" height="100%" overflow="hidden" flexDirection="column">
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={errorModalRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setErrorDialog(null);
					}
				}}
				open={Boolean(errorDialog)}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									{errorDialog?.header}
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>{errorDialog?.body}</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={errorModalRef}
										ml={3}
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => setErrorDialog(null)}
									>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={confirmChangesRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setConfirmChanges(false);
					}
				}}
				open={confirmChanges}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Confirm Changes
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>Are you sure you want to save your changes?</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Button
									colorPalette="red"
									ref={confirmChangesRef}
									color={theme.colors.white}
									bg={theme.colors.red[500]}
									_hover={{ background: theme.colors.red[600] }}
									onClick={() => {
										setActiveLog('');
										setConfirmChanges(false);
										setSaveChanges([]);
										setEditMode(false);

										if (nextNavigation) {
											nextNavigation();
											setNextNavigation(null);
										} else {
											resetForm().then(() => setCurrentFormId(currentFormId));
										}
									}}
								>
									No
								</Button>
								<Dialog.ActionTrigger asChild>
									<Button
										ml={3}
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={async () => {
											setConfirmChanges(false);

											try {
												for (let changes of saveChanges) {
													setPacketList(prevPacketList =>
														prevPacketList.map(packet => ({
															...packet,
															packet_forms: packet.packet_forms.map(form =>
																packet.packet_id === changes.packetId &&
																form.id === changes.data.id
																	? {
																			...changes.data,
																			form_data: JSON.stringify(
																				changes.data.form_data
																			)
																		}
																	: form
															)
														}))
													);

													await savePacketForm(changes.packetId, changes.data);
												}

												setSaveChanges([]);
												setEditMode(false);

												if (nextNavigation) {
													nextNavigation();
													setNextNavigation(null);
												}
											} catch (error) {
												setErrorDialog({
													header: 'Error Saving Changes',
													body: error.message
												});
											}
										}}
									>
										Yes
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={signatureMissingRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setSignatureMissingDialogOpen(false);
					}
				}}
				open={signatureMissingDialogOpen}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Signature Missing
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>Please sign this form and print name before continuing.</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={signatureMissingRef}
										onClick={() => setSignatureMissingDialogOpen(false)}
									>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={signatureRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setSignatureDialogOpen(false);
					}
				}}
				open={signatureDialogOpen}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Signature Missing
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>Hazardous Energy LOTO form is unsigned.</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button ref={signatureRef} onClick={() => setSignatureDialogOpen(false)}>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>

			{/*Header*/}
			<HStack p={4} backgroundColor={theme.colors.blue[700]}>
				<Image h="50px" src="/themes/custom/msiforms/images/msi-logo.png" />
				<Spacer />
				<HStack spacing={4}>
					<Link
						fontWeight={tab === 'project-forms' ? 'bold' : 'normal'}
						paddingRight={4}
						borderRight={`1px solid ${theme.colors.white}`}
						color={theme.colors.white}
						href="#"
						onClick={e => {
							e.preventDefault();

							handleNavChange(() => {
								changeNavTab('project-forms');

								if (tab !== 'project-forms') {
									window.history.pushState(
										window.history.state,
										'',
										`${window.location.origin}/dashboard`
									);
								}
							});
						}}
					>
						Project Forms
					</Link>
					<Link
						paddingRight={4}
						borderRight={`1px solid ${theme.colors.white}`}
						color={theme.colors.white}
						href="#"
						onClick={e => {
							e.preventDefault();

							handleNavChange(() => {
								window.location.assign(`${window.location.origin}/msi_search`);
							});
						}}
					>
						Advanced Search
					</Link>
					<Link
						fontWeight={tab === 'unassigned' ? 'bold' : 'normal'}
						paddingRight={4}
						borderRight={`1px solid ${theme.colors.white}`}
						color={theme.colors.white}
						href="#"
						onClick={e => {
							e.preventDefault();

							handleNavChange(() => {
								changeNavTab('unassigned');

								if (tab !== 'unassigned') {
									window.history.pushState(
										window.history.state,
										'',
										`${window.location.origin}/dashboard/unassigned`
									);
								}
							});
						}}
					>
						Unassigned Packets
					</Link>
					<Link
						fontWeight="normal"
						paddingRight={4}
						borderRight={`1px solid ${theme.colors.white}`}
						color={theme.colors.white}
						href="#"
						onClick={e => {
							e.preventDefault();
							handleNavChange(() => {
								window
									.open(
										'https://www.wrike.com/workspace.htm?acc=6543771#/forms?formid=2521332',
										'_blank'
									)
									.focus();
							});
						}}
					>
						Safety Forms
					</Link>
					<Link
						color={theme.colors.white}
						href="#"
						onClick={e => {
							e.preventDefault();

							handleNavChange(() => {
								setCurrentProject('');
								setCurrentPhase({});
								setError(null);
								localStorage.setItem('isLoggedIn', false);

								Sentry.setUser(null);

								window.location.replace('/user/logout');
							});
						}}
					>
						Sign Out
					</Link>
				</HStack>
			</HStack>
			<Flex height="100%" overflow="hidden" position="relative">
				{/*Navigation*/}
				{navEnabled ? (
					<>
						<Flex
							as="nav"
							height="100%"
							backgroundColor={theme.colors.gray[500]}
							width={navWidth}
							flexDirection="column"
							position={navDrawerEnabled ? 'absolute' : 'static'}
							top={0}
							left={navDrawerEnabled && navDrawerOpen ? 0 : `-${navWidth}px`}
							zIndex={20}
							transition="400ms"
							transitionTimingFunction="ease-in-out"
							display="flex"
						>
							<Box>
								<Heading color={theme.colors.blue[700]} px="12px" pt="12px">
									{tab === 'project-forms'
										? 'Projects'
										: tab === 'unassigned'
											? 'Unassigned Packets'
											: tabLabels[tab]}
								</Heading>
								<HStack m="12px">
									<InputGroup
										w={400}
										endElement={
											<ChakraIcon
												size="21px"
												opacity={tab === 'unassigned' ? 0.4 : 1}
												color="gray.400"
											>
												<FaSearch />
											</ChakraIcon>
										}
									>
										<Input
											value={searchQuery}
											onChange={({ target }) => setSearchQuery(target.value)}
											placeholder="Search"
											backgroundColor={theme.colors.white}
											disabled={tab === 'unassigned' && !assignPacket}
										/>
									</InputGroup>
									{tab === 'project-forms' && !assignPacket ? (
										<>
											<Switch.Root
												size="lg"
												onCheckedChange={() => setSearchArchives(prevState => !prevState)}
												checked={searchArchives}
												colorPalette="blue"
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text fontWeight={searchArchives ? 'bold' : 'normal'}>
												Inactive Projects
											</Text>
										</>
									) : tab === 'unassigned' && !assignPacket ? (
										<Button
											mr="12px"
											size="sm"
											color={theme.colors.white}
											bg={theme.colors.blue[700]}
											_hover={{ backgroundColor: theme.colors.blue[800] }}
											variant="solid"
											onClick={() => {
												if (tab === 'unassigned') {
													createNewPacket(dataAPI, 'packet', packetList)
														.then(async newPacket => {
															await resetForm();
															return newPacket;
														})
														.then(newPacket => {
															setPacketList(prevPacketList => [
																newPacket,
																...prevPacketList
															]);

															setCurrentPacketId(newPacket.packet_id);
															setCurrentFormId('supervisor_handoff');
															setFormOptionsDialog(false);
														})
														.catch(error => {
															setErrorDialog({
																header: 'Error Creating New Packet',
																body: error.message
															});
														});
												}
											}}
										>
											Create New
										</Button>
									) : (
										<>
											<Button
												mr="12px"
												size="sm"
												color={theme.colors.white}
												bg={theme.colors.red[500]}
												_hover={{ backgroundColor: theme.colors.red[600] }}
												variant="solid"
												onClick={() => {
													setAssignTo({});
													setSearchQuery('');
													setAssignPacket(false);
												}}
											>
												Cancel
											</Button>
											<Button
												mr="12px"
												size="sm"
												color={theme.colors.white}
												bg={theme.colors.blue[700]}
												_hover={{ backgroundColor: theme.colors.blue[800] }}
												variant="solid"
												disabled={!Object.keys(assignTo).length}
												onClick={() => setAssignConfirmation(true)}
											>
												Assign Packet
											</Button>
											<Dialog.Root
												role="alertdialog"
												initialFocusEl={assignPacketRef}
												onOpenChange={({ open }) => {
													if (!open) {
														setAssignConfirmation(false);
													}
												}}
												open={assignConfirmation}
												placement="center"
											>
												<Portal>
													<Dialog.Backdrop />
													<Dialog.Positioner>
														<Dialog.Content opacity="1 !important">
															<Dialog.Header>
																<Dialog.Title fontSize="lg" fontWeight="bold">
																	Confirm Assignment
																</Dialog.Title>
															</Dialog.Header>
															<Dialog.Body>
																<p>{`Are you sure you want to assign packet to ${assignTo?.projectNumber}-${assignTo?.phaseNumber}`}</p>
															</Dialog.Body>
															<Dialog.Footer>
																<Dialog.ActionTrigger asChild>
																	<Button
																		colorPalette="red"
																		ref={assignPacketRef}
																		color={theme.colors.white}
																		bg={theme.colors.red[500]}
																		_hover={{ background: theme.colors.red[600] }}
																		onClick={() => setAssignConfirmation(false)}
																	>
																		Cancel
																	</Button>
																</Dialog.ActionTrigger>
																<Button
																	ml={3}
																	color={theme.colors.white}
																	bg={theme.colors.blue[700]}
																	_hover={{ backgroundColor: theme.colors.blue[800] }}
																	onClick={handleAssign}
																>
																	Assign Packet
																</Button>
															</Dialog.Footer>
															<Dialog.CloseTrigger asChild>
																<CloseButton size="sm" />
															</Dialog.CloseTrigger>
														</Dialog.Content>
													</Dialog.Positioner>
												</Portal>
											</Dialog.Root>
										</>
									)}
								</HStack>
								<Separator bgColor={theme.colors.white} height="3px" />
							</Box>
							<Flex flex={1}>
								<AutoSizer>
									{({ height, width }) =>
										tab === 'project-forms' ? (
											!assignPacket ? (
												<VariableSizeList
													ref={variableSizeListRef}
													height={height}
													width={width}
													itemCount={navigationList.length}
													itemSize={getListHeight}
												>
													{Project}
												</VariableSizeList>
											) : (
												<FixedSizeList
													height={height}
													width={width}
													itemCount={navigationList.length}
													itemSize={100}
												>
													{AssignPacketList}
												</FixedSizeList>
											)
										) : tab === 'unassigned' ? (
											!assignPacket ? (
												packetListLoading ? (
													<Spinner ml="20px" mt="34px" />
												) : (
													<FixedSizeList
														height={height}
														width={width}
														itemCount={packetList.length}
														itemSize={100}
													>
														{UnassignedPacket}
													</FixedSizeList>
												)
											) : (
												<FixedSizeList
													height={height}
													width={width}
													itemCount={navigationList.length}
													itemSize={100}
												>
													{AssignPacketList}
												</FixedSizeList>
											)
										) : null
									}
								</AutoSizer>
							</Flex>
						</Flex>
						{navDrawerEnabled && (
							<>
								<Button
									onClick={() => setNavDrawerOpen(prevState => !prevState)}
									position="absolute"
									top="50%"
									bottom="50%"
									left={navDrawerOpen ? `${navWidth}px` : 0}
									zIndex={15}
									bg={theme.colors.blue[700]}
									_hover={{ backgroundColor: theme.colors.blue[800] }}
									color={theme.colors.white}
									borderRadius="4px"
									transition="400ms"
									transitionTimingFunction="ease-in-out"
									p="5px 5px 5px 8px"
								>
									{navDrawerOpen ? (
										<ChakraIcon w="30px" h="30px">
											<FaArrowLeft />
										</ChakraIcon>
									) : (
										<ChakraIcon w="30px" h="30px">
											<FaArrowRight />
										</ChakraIcon>
									)}
								</Button>
								{navDrawerOpen && (
									<Box
										position="fixed"
										top="82px"
										left={0}
										right={0}
										bottom={0}
										opacity={1}
										zIndex={5}
										bg="rgba(50, 50, 50, 0.75)"
										onClick={() => setNavDrawerOpen(false)}
									/>
								)}
							</>
						)}
					</>
				) : null}
				<Box flex={1} overflowY="auto" overflowX="hidden">
					{tab === 'safety-database' && window.canAccessSafetyDatabase ? (
						<SafetyDatabase />
					) : (tab === 'project-forms' || tab === 'unassigned') && currentPacket ? (
						attachmentsIndex !== null ? (
							<FormAttachments
								packetTitle={currentPacket.title}
								activeLog={activeLog}
								currentProject={currentProject}
								currentPhase={currentPhase.phase}
								currentPacketId={currentPacketId}
								currentFormId={currentFormId}
								attachmentsIndex={attachmentsIndex}
								setAttachmentsIndex={setAttachmentsIndex}
								attachments={currentPacket.packet_attachments}
								saveForm={saveForm}
								navDrawerEnabled={navDrawerEnabled}
								navDrawerOpen={navDrawerOpen}
								readOnly={currentPacketSubmitted}
							/>
						) : (
							currentPacket &&
							currentForm && (
								<FormEditor
									activeLog={activeLog}
									saveChanges={saveChanges}
									formData={
										saveChanges.find(
											changes =>
												currentPacketId === changes.packetId &&
												currentFormId === changes.data.form_id
										)?.form_data || formData
									}
									formDefinition={formDefinition}
									currentPacket={currentPacket}
									currentForm={currentForm}
									hasDailyPackets={Boolean(dailyPacketsLength)}
									dataAPI={dataAPI}
									projectInfoCrew={projectInfoCrew}
									supervisorPacketFormsList={supervisorPacketFormsList}
									handoffData={handoffData}
									setActiveLog={setActiveLog}
									setAttachmentsIndex={setAttachmentsIndex}
									saveForm={currentPacketSubmitted && editMode ? queueFormChanges : saveForm}
									currentPacketSubmitted={currentPacketSubmitted}
									submitting={submitting}
									editMode={editMode}
									setEditMode={setEditMode}
									isUnassigned={tab === 'unassigned'}
								/>
							)
						)
					) : tab === 'project-forms' && currentDriverExemption !== null ? (
						<DriverExemptionForm
							record={driverExemptionRecords[currentDriverExemption]}
							currentProject={currentProject}
						/>
					) : null}
				</Box>
			</Flex>
		</Flex>
	);
}
